/**
 * User Settings Management
 * Handles reading/writing user preferences to Firestore
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import type { User } from 'firebase/auth';

const COLLECTION = 'users';

/**
 * Get user settings from Firestore
 * Returns null if settings don't exist yet
 */
export async function getUserSettings(userId: string): Promise<UserSettings | null> {
    try {
        const db = getFirestore(getApp());
        const docRef = doc(db, COLLECTION, userId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                id: docSnap.id,
                userId: docSnap.id,
                selectedPersonaId: data.selectedPersonaId || null,
                preferredChronology: data.preferredChronology || 'Textual',
                hasCompletedOnboarding: data.hasCompletedOnboarding || false,
                hasSeenAIOnboarding: data.hasSeenAIOnboarding || false,
                hasSeenWalkthrough: data.hasSeenWalkthrough || false,
                email: data.email || null,
                displayName: data.displayName || null,
                photoURL: data.photoURL || null,
                isAnonymous: data.isAnonymous || false,
                userAgent: data.userAgent || 'unknown',
                browser: data.browser || 'unknown',
                os: data.os || 'unknown',
                device: data.device || 'unknown',
                windowSize: data.windowSize || { width: 0, height: 0 },
                lastLogin: data.lastLogin?.toDate() || data.dateUpdated?.toDate() || new Date(),
                dateCreated: data.dateCreated?.toDate() || new Date(),
                dateUpdated: data.dateUpdated?.toDate() || new Date(),
            };
        }

        return null;
    } catch (error) {
        console.error('Error getting user settings:', error);
        return null;
    }
}

/**
 * Create initial user settings document
 */
export async function createUserSettings(user: User): Promise<UserSettings> {
    const db = getFirestore(getApp());
    const docRef = doc(db, COLLECTION, user.uid);

    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
    const { browser, os, device } = parseUserAgent(ua);

    const newSettings = {
        userId: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        isAnonymous: user.isAnonymous,
        userAgent: ua,
        browser,
        os,
        device,
        windowSize: typeof window !== 'undefined' ? { width: window.innerWidth, height: window.innerHeight } : { width: 0, height: 0 },
        selectedPersonaId: null,
        preferredChronology: 'Textual',
        hasCompletedOnboarding: false,
        hasSeenAIOnboarding: false,
        hasSeenWalkthrough: false,
        dateCreated: serverTimestamp(),
        dateUpdated: serverTimestamp(),
        lastLogin: serverTimestamp(),
    };

    await setDoc(docRef, newSettings, { merge: true });

    return {
        id: user.uid,
        userId: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        isAnonymous: user.isAnonymous,
        selectedPersonaId: null,
        preferredChronology: 'Textual',
        hasCompletedOnboarding: false,
        hasSeenAIOnboarding: false,
        hasSeenWalkthrough: false,
        dateCreated: new Date(),
        dateUpdated: new Date(),
        lastLogin: new Date(),
        userAgent: ua,
        browser,
        os,
        device,
        windowSize: typeof window !== 'undefined' ? { width: window.innerWidth, height: window.innerHeight } : { width: 0, height: 0 },
    };
}

/**
 * Sync user profile info and analytics on login
 */
export async function syncUserSettings(user: User): Promise<void> {
    try {
        const db = getFirestore(getApp());
        const docRef = doc(db, COLLECTION, user.uid);

        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
        const { browser, os, device } = parseUserAgent(ua);

        await setDoc(docRef, {
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            isAnonymous: user.isAnonymous,
            userAgent: ua,
            browser,
            os,
            device,
            windowSize: typeof window !== 'undefined' ? { width: window.innerWidth, height: window.innerHeight } : { width: 0, height: 0 },
            dateUpdated: serverTimestamp(),
            lastLogin: serverTimestamp(),
        }, { merge: true });
    } catch (error) {
        console.error('Error syncing user settings:', error);
    }
}

/**
 * Update user's selected persona
 */
export async function updateSelectedPersona(userId: string, personaId: string): Promise<void> {
    const db = getFirestore(getApp());
    const docRef = doc(db, COLLECTION, userId);

    await setDoc(docRef, {
        selectedPersonaId: personaId,
        dateUpdated: serverTimestamp(),
    }, { merge: true });
}

/**
 * Mark onboarding as complete
 */
export async function completeOnboarding(
    userId: string,
    selectedPersonaId: string,
    preferredChronology: BiblicalChronology = 'Textual'
): Promise<void> {
    const db = getFirestore(getApp());
    const docRef = doc(db, COLLECTION, userId);

    await setDoc(docRef, {
        selectedPersonaId,
        preferredChronology,
        hasCompletedOnboarding: true,
        hasSeenAIOnboarding: true,
        hasSeenWalkthrough: true, // If they finished onboarding, they've likely seen the walkthrough or don't need it
        dateUpdated: serverTimestamp(),
    }, { merge: true });
}

/**
 * Mark AI onboarding as seen
 */
export async function dismissAIOnboarding(userId: string): Promise<void> {
    const db = getFirestore(getApp());
    const docRef = doc(db, COLLECTION, userId);

    await setDoc(docRef, {
        hasSeenAIOnboarding: true,
        dateUpdated: serverTimestamp(),
    }, { merge: true });
}

/**
 * Mark walkthrough as seen (or unseen for testing)
 */
export async function dismissWalkthrough(userId: string, seen: boolean = true): Promise<void> {
    const db = getFirestore(getApp());
    const docRef = doc(db, COLLECTION, userId);

    await setDoc(docRef, {
        hasSeenWalkthrough: seen,
        dateUpdated: serverTimestamp(),
    }, { merge: true });
}

/**
 * Update user's preferred chronology
 */
export async function updatePreferredChronology(userId: string, chronology: BiblicalChronology): Promise<void> {
    const db = getFirestore(getApp());
    const docRef = doc(db, COLLECTION, userId);

    await setDoc(docRef, {
        preferredChronology: chronology,
        dateUpdated: serverTimestamp(),
    }, { merge: true });
}

/**
 * Get or create user settings
 * Ensures settings document exists for the user
 */
export async function getOrCreateUserSettings(user: User): Promise<UserSettings> {
    let settings = await getUserSettings(user.uid);

    if (!settings) {
        settings = await createUserSettings(user);
    } else {
        // Update existing profile info
        await syncUserSettings(user);
    }

    return settings;
}

/**
 * Helper to extract browser/OS info from User Agent
 */
function parseUserAgent(ua: string) {
    let browser = 'Unknown';
    let os = 'Unknown';
    let device = 'Desktop';

    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('SamsungBrowser')) browser = 'Samsung Browser';
    else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
    else if (ua.includes('Trident')) browser = 'Internet Explorer';
    else if (ua.includes('Edge')) browser = 'Edge';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';

    if (ua.includes('Win')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('X11')) os = 'UNIX';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone')) os = 'iOS';

    if (ua.includes('Mobi')) device = 'Mobile';
    if (ua.includes('Tablet')) device = 'Tablet';
    if (ua.includes('iPad')) device = 'Tablet';

    // Specific overrides
    if (ua.includes('Android')) device = 'Android';
    if (ua.includes('iPhone')) device = 'iPhone';
    if (ua.includes('iPad')) device = 'iPad';

    return { browser, os, device };
}
